// https://www.hollandwinkel.nl/media/catalog/product/cache/831b25158591b46ca6e0d32ee6983c83/i/m/img_20190303_155827_clipped_rev_1.jpeg
import axios from 'axios';

import { DEFAULT_CURRENCY_ISO } from '@rover/constants/src/i18n/language';
import { PRICE_FILTER } from '@rover/constants/src/search/searchPage.constants';
import { SERVICE_TYPE_DESCRIPTION } from '@rover/constants/src/translations/service.constants';
import { OVERNIGHT_BOARDING, SERVICES } from '@rover/constants/src/translations/services.constants';
import APIError from '@rover/react-lib/src/apis/APIError';
import { get, options as searchOptions } from '@rover/react-lib/src/apis/search.api';
import fetchSearchResponseFactory from '@rover/react-lib/src/factories/fetchSearchResponseFactory';
import type {
  ActionCreator,
  Duck,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/src/lib/redux-duck';
import { createDuck } from '@rover/react-lib/src/lib/redux-duck';
import buildAsRequesterRelationshipUrl from '@rover/react-lib/src/pages/search/SearchPage/utilities/buildAsRequesterRelationshipUrl';
import parseDate from '@rover/react-lib/src/pages/search/SearchPage/utilities/parseDate';
import { effects as favoriteSittersEffects } from '@rover/react-lib/src/redux/ducks/favoriteSitters.duck';
import { selectors as frontendConfigurationSelectors } from '@rover/react-lib/src/redux/ducks/frontendConfiguration.duck';
import type { SearchHeader } from '@rover/rsdk/src/apiClient/latest';
import { OptimizedSearchResponse, OptimizedSearchResult } from '@rover/rsdk/src/apiClient/latest';
import { captureError } from '@rover/rsdk/src/modules/ErrorReporting';
import { kebabifyString } from '@rover/rsdk/src/modules/Network/object-utilities';
import type {
  Callout,
  PaginatedCallout,
  Pagination,
  PriceTransparencyLevel,
  SearchFilters,
  SearchFiltersOptions,
  SearchMapData,
  SearchOptionsResponse,
  SearchResult,
} from '@rover/types';
import { SearchDerivedData } from '@rover/types/src/apiResponses/FetchSearchResponse';
import filterShim from '@rover/utilities/filterShim';

import generateActions from './generateActions';

export type State = {
  errorMessage: string;
  fetchSearchResponse: OptimizedSearchResponse;
  loading: boolean;
  options: Record<string, SearchFiltersOptions>;
  searchInitiatedByMap: boolean;
  isShowingPriceInCurrency: boolean;
};

type Actions = {
  fetchSearchPending: ActionCreator<{
    searchInitiatedByMap: boolean;
  }>;
  fetchSearchSuccess: ActionCreator<OptimizedSearchResponse>;
  fetchSearchFailure: ActionCreator<string>;
  fetchSearchOptionsPending: ActionCreator<void>;
  fetchSearchOptionsSuccess: ActionCreator<{
    searchOptionsResponse: SearchOptionsResponse;
    serviceType?: string;
    allServices?: boolean;
  }>;
  fetchSearchOptionsFailure: ActionCreator<string>;
  updateFavoritePending: ActionCreator<string>;
  updateFavoriteSuccess: ActionCreator<void>;
  updateFavoriteFailure: ActionCreator<{
    personOpk: string;
    originalFavoriteValue: boolean;
  }>;
  toggleIsShowingPriceInCurrency: ActionCreator<boolean>;
};

type Effects = {
  fetchSearch: EffectCreator<{
    filters?: Partial<SearchFilters>;
    isInitialSearch?: boolean;
    searchInitiatedByMap?: boolean;
  }>;
  fetchSearchOptions: EffectCreator<{
    serviceType?: string;
    allServices?: boolean;
  }>;
  toggleFavorite: EffectCreator<string>;
};

type Selectors = {
  getSearchQuery: SelectorCreator<SearchFilters | undefined>;
  getSearchResponse: SelectorCreator<OptimizedSearchResponse>;
  getSearchResponseTotalNumberOfResults: SelectorCreator<number>;
  getSearchResults: SelectorCreator<SearchResult[]>;
  getSearchHeader: SelectorCreator<SearchHeader>;
  getSelectedProvider: SelectorCreator<SearchResult>;
  getHasSelectedProvider: SelectorCreator<boolean>;
  getShouldPreferCatReviews: SelectorCreator<boolean>;
  getSearchCurrencyCode: SelectorCreator<string>;
  getSearchPriceFilterMaxValue: SelectorCreator<number>;
  getSearchPriceFilterMinValue: SelectorCreator<number>;
  getPersonIsFavorite: SelectorCreator<(arg0: string) => boolean>;
  getAlternateSearchResults: SelectorCreator<SearchResult[]>;
  getAlternateServiceName: SelectorCreator<string>;
  getAlternateServiceDescription: SelectorCreator<string>;
  getBestMatchSearchResults: SelectorCreator<SearchResult[]>;
  getBestMatchConfigs: SelectorCreator<string[]>;
  getIsBestMatchExperience: SelectorCreator<boolean>;
  getSearchCallouts: SelectorCreator<Callout[]>;
  getSearchPaginatedCallouts: SelectorCreator<PaginatedCallout[]>;
  getSearchCalloutsByIndex: SelectorCreator<Record<number, Callout>>;
  getSearchFiltersOptions: SelectorCreator<Record<string, SearchFiltersOptions>>;
  getPagination: SelectorCreator<Pagination>;
  getSearchIsLoading: SelectorCreator<boolean>;
  getSearchErrorMessage: SelectorCreator<string>;
  getHasPets: SelectorCreator<boolean>;
  getZoomLevel: SelectorCreator<number | null | undefined>;
  getCenterLatLng: SelectorCreator<
    | {
        lat: string;
        lng: string;
      }
    | null
    | undefined
  >;
  getExcludedPersonOpks: SelectorCreator<string[] | undefined>;
  getSearchInitiatedByMap: SelectorCreator<boolean>;
  getMapData: SelectorCreator<SearchMapData>;
  getQueryPetTypes: SelectorCreator<string[]>;
  getQueryDateRange: SelectorCreator<Record<'startDate' | 'endDate', Date | undefined>>;
  getQueryDistinctDates: SelectorCreator<Record<'dates', string | undefined>>;
  getQueryDerivedData: SelectorCreator<SearchDerivedData>;
  getSearchResultsHasFacilities: SelectorCreator<boolean>;
  getPriceTransparencyLevel: SelectorCreator<PriceTransparencyLevel>;
  getIsShowingPriceInCurrency: SelectorCreator<boolean>;
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

let cancelTokenSource;

if (process.env.JS_ENV_CLIENT) {
  cancelTokenSource = axios.CancelToken.source();
}

const setMatchingPersonsFavorite = (
  people: OptimizedSearchResult[],
  personOpk: string,
  favoriteValue: boolean | null | undefined
): OptimizedSearchResult[] =>
  people.map((result) => {
    if (personOpk === result.personOpk) {
      return {
        ...result,
        // if a favoriteValue is provided then use it, otherwise toggle the existing value
        isFavorite: typeof favoriteValue === 'boolean' ? favoriteValue : !result.isFavorite,
      };
    }

    return result;
  });

const removeUndefinedFilterValues = (filters: Partial<SearchFilters>): Partial<SearchFilters> =>
  Object.keys(filters).reduce((processed, key) => {
    const value = filters[key];

    if (value === undefined) {
      return processed;
    }

    return { ...processed, [key]: value };
  }, {});

export const options: Options = {
  name: 'search',
  initialState: {
    errorMessage: '',
    fetchSearchResponse: fetchSearchResponseFactory(),
    loading: false,
    options: SERVICES().reduce((opts, service) => {
      const clone = { ...opts };
      clone[service.type] = {};
      return clone;
    }, {}),
    searchInitiatedByMap: false,
    isShowingPriceInCurrency: false,
  },
  actions: {
    ...generateActions('fetchSearch'),
    fetchSearchPending:
      ({ searchInitiatedByMap }) =>
      (state) => ({
        ...state,
        loading: true,
        errorMessage: '',
        searchInitiatedByMap,
      }),
    fetchSearchOptionsPending: () => (state) => ({ ...state, errorMessage: '' }),
    fetchSearchOptionsSuccess:
      ({ searchOptionsResponse, serviceType, allServices }) =>
      (state) => {
        if (allServices) {
          const modifiedSearchOptions = Object.entries(
            filterShim(searchOptionsResponse).actions.GET
          ).reduce((acc, [key, value]) => ({ ...acc, [kebabifyString(key)]: value }), {});
          return {
            ...state,
            options: { ...state.options, ...modifiedSearchOptions },
            errorMessage: '',
          };
        }

        if (serviceType) {
          return {
            ...state,
            options: {
              ...state.options,
              [serviceType]: filterShim(searchOptionsResponse).actions.GET,
            },
            errorMessage: '',
          };
        }

        return { ...state, errorMessage: '' };
      },
    fetchSearchOptionsFailure: (errorMessage) => (state) => ({ ...state, errorMessage }),
    updateFavoritePending: (personOpk) => (state) => ({
      ...state,
      fetchSearchResponse: {
        ...state.fetchSearchResponse,
        results: setMatchingPersonsFavorite(
          state.fetchSearchResponse.results,
          personOpk,
          undefined
        ),
        alternateSearch: {
          ...state.fetchSearchResponse.alternateSearch,
          results: setMatchingPersonsFavorite(
            state.fetchSearchResponse.alternateSearch?.results as OptimizedSearchResult[],
            personOpk,
            undefined
          ),
        },
        bestMatchSearch: {
          ...state.fetchSearchResponse.bestMatchSearch,
          results: setMatchingPersonsFavorite(
            state.fetchSearchResponse.bestMatchSearch?.results as OptimizedSearchResult[],
            personOpk,
            undefined
          ),
        },
      },
    }),
    updateFavoriteSuccess: () => (state) => ({ ...state }),
    updateFavoriteFailure:
      ({ personOpk, originalFavoriteValue }) =>
      (state) => ({
        ...state,
        fetchSearchResponse: {
          ...state.fetchSearchResponse,
          results: setMatchingPersonsFavorite(
            state.fetchSearchResponse.results,
            personOpk,
            originalFavoriteValue
          ),
          alternateSearch: {
            ...state.fetchSearchResponse.alternateSearch,
            results: setMatchingPersonsFavorite(
              state.fetchSearchResponse.alternateSearch?.results as OptimizedSearchResult[],
              personOpk,
              originalFavoriteValue
            ),
          },
          bestMatchSearch: {
            ...state.fetchSearchResponse.bestMatchSearch,
            results: setMatchingPersonsFavorite(
              state.fetchSearchResponse.bestMatchSearch?.results as OptimizedSearchResult[],
              personOpk,
              originalFavoriteValue
            ),
          },
        },
      }),
    toggleIsShowingPriceInCurrency: (toggle) => (state) => ({
      ...state,
      isShowingPriceInCurrency: toggle,
    }),
  },
  effects: {
    fetchSearch:
      ({ filters, isInitialSearch, searchInitiatedByMap = false }) =>
      // @ts-expect-error
      async (
        dispatch,
        getState,
        { fetchSearchPending, fetchSearchSuccess, fetchSearchFailure },
        duckEffects,
        { getSearchIsLoading }
      ): Promise<OptimizedSearchResponse | null | undefined> => {
        if (getSearchIsLoading(getState())) {
          cancelTokenSource &&
            cancelTokenSource.cancel('Operation cancelled by user initiating a new search.');
          cancelTokenSource = axios.CancelToken.source();
        } else {
          dispatch(
            fetchSearchPending({
              searchInitiatedByMap,
            })
          );
        }

        const filtersToUse = {
          ...filters,
          isInitialSearch: isInitialSearch || false,
        };
        const processedFilters = removeUndefinedFilterValues(filtersToUse);

        try {
          const fetchSearchResponse: OptimizedSearchResponse | null | undefined = await get(
            cancelTokenSource?.token,
            processedFilters
          );

          // Cheeky way of setting different image sizes in SSR Search without affecting the django and mobile apps
          // Ticket to undo this quick fix: https://roverdotcom.atlassian.net/browse/DEV-53902
          if (fetchSearchResponse && fetchSearchResponse.results) {
            fetchSearchResponse.results = fetchSearchResponse.results.map((result) => {
              const updatedResult = {
                ...result,
                defaultImage: {
                  small: result.defaultImage.small.replace(
                    'width=200&height=200',
                    'width=150&height=150'
                  ),
                  medium: result.defaultImage.medium.replace(
                    'width=450&height=450',
                    'width=300&height=300'
                  ),
                },
              };

              // A cheeky fix for DEV-53993 that can be undone with https://roverdotcom.atlassian.net/browse/DEV-53902 as well
              if (
                updatedResult.reviewDogPhotoUrl &&
                updatedResult.reviewDogPhotoUrl.includes('width=100&height=100')
              ) {
                updatedResult.reviewDogPhotoUrl = updatedResult.reviewDogPhotoUrl.replace(
                  'width=100&height=100',
                  'width=72&height=72'
                );
              }

              return updatedResult;
            });
          }

          if (fetchSearchResponse) {
            dispatch(fetchSearchSuccess(fetchSearchResponse));
            return fetchSearchResponse;
          }
        } catch (e) {
          if ((e as APIError).status >= 400 && (e as APIError).status < 500) {
            dispatch(fetchSearchFailure((e as APIError).message));
          } else {
            dispatch(fetchSearchFailure((e as APIError).message));
            captureError(e as APIError);
          }
        }

        return undefined;
      },
    fetchSearchOptions:
      (
        {
          serviceType,
          allServices,
        }: {
          serviceType?: string;
          allServices?: boolean;
        } = {
          serviceType: OVERNIGHT_BOARDING().type,
          allServices: false,
        }
      ) =>
      async (
        dispatch,
        getState,
        { fetchSearchOptionsPending, fetchSearchOptionsSuccess, fetchSearchOptionsFailure },
        duckEffects,
        { getSearchFiltersOptions }
      ) => {
        if (
          (serviceType &&
            SERVICES().some((s) => s.type === serviceType) &&
            !Object.entries(getSearchFiltersOptions(getState())[serviceType]).length) ||
          allServices
        ) {
          dispatch(fetchSearchOptionsPending());

          try {
            const searchOptionsResponse: SearchOptionsResponse = await searchOptions(
              serviceType,
              allServices
            );
            dispatch(
              fetchSearchOptionsSuccess({
                searchOptionsResponse,
                serviceType,
                allServices,
              })
            );
          } catch (e) {
            dispatch(fetchSearchOptionsFailure((e as APIError).message));
            captureError(e as APIError);
          }
        }
      },
    toggleFavorite:
      (personOpk: string) =>
      async (
        dispatch,
        getState,
        { updateFavoritePending, updateFavoriteSuccess, updateFavoriteFailure },
        duckEffects,
        { getPersonIsFavorite }
      ) => {
        const currentFavoriteValue = getPersonIsFavorite(getState())(personOpk);
        const asRequesterRelationshipUrl = buildAsRequesterRelationshipUrl(personOpk);
        dispatch(updateFavoritePending(personOpk));

        try {
          dispatch(
            favoriteSittersEffects.toggleFavorite({
              asRequesterRelationshipUrl,
              isFavorite: !currentFavoriteValue,
            })
          );
          dispatch(updateFavoriteSuccess());
        } catch (e) {
          dispatch(
            updateFavoriteFailure({
              personOpk,
              originalFavoriteValue: currentFavoriteValue,
            })
          );
        }
      },
  },
  selectors: {
    getSearchQuery: (getState, createSelector) =>
      createSelector([getState], ({ fetchSearchResponse }) => fetchSearchResponse.query),
    getSearchResults: (getState, createSelector) =>
      createSelector([getState], ({ fetchSearchResponse }) => fetchSearchResponse.results),
    getSearchHeader: (getState, createSelector) =>
      createSelector([getState], ({ fetchSearchResponse }) => fetchSearchResponse.header),
    getSelectedProvider: (getState, createSelector) =>
      createSelector([getState], ({ fetchSearchResponse }) => fetchSearchResponse.selectedProvider),
    getHasSelectedProvider: (getState, createSelector, { getSelectedProvider }) =>
      createSelector([getSelectedProvider], (selectedProvider) => !!selectedProvider),
    getAlternateSearchResults: (getState, createSelector) =>
      createSelector(
        [getState],
        ({ fetchSearchResponse }) => fetchSearchResponse?.alternateSearch?.results
      ),
    getBestMatchSearchResults: (getState, createSelector) =>
      createSelector(
        [getState],
        ({ fetchSearchResponse }) => fetchSearchResponse?.bestMatchSearch?.results
      ),
    getBestMatchConfigs: (getState, createSelector) =>
      createSelector(
        [getState],
        ({ fetchSearchResponse }) => fetchSearchResponse?.bestMatchSearch?.data?.configs ?? []
      ),
    getPersonIsFavorite: (
      getState,
      createSelector,
      { getSearchResults, getAlternateSearchResults, getBestMatchSearchResults }
    ) =>
      createSelector(
        [getSearchResults, getAlternateSearchResults, getBestMatchSearchResults],
        (searchResults, alternateSearchResults, bestMatchSearchResults) => (personOpk) =>
          [...searchResults, ...alternateSearchResults, ...bestMatchSearchResults].find(
            (searchResult) => searchResult.personOpk === personOpk
          )?.isFavorite ?? false
      ),
    getAlternateServiceName: (getState, createSelector) =>
      createSelector(
        [getState],
        ({ fetchSearchResponse }) => fetchSearchResponse?.alternateSearch?.service?.name
      ),
    getAlternateServiceDescription: (getState, createSelector) =>
      createSelector(
        [getState],
        ({ fetchSearchResponse }) =>
          SERVICE_TYPE_DESCRIPTION()[fetchSearchResponse?.alternateSearch?.service?.slug]
      ),
    getSearchCallouts: (getState, createSelector) =>
      createSelector([getState], ({ fetchSearchResponse }) => fetchSearchResponse.callouts || []),
    getSearchPaginatedCallouts: (getState, createSelector) =>
      createSelector(
        [getState],
        ({ fetchSearchResponse }) => fetchSearchResponse.paginatedCallouts
      ),
    getSearchCalloutsByIndex: (getState, createSelector, { getSearchCallouts }) =>
      createSelector([getSearchCallouts], (searchCallouts) =>
        searchCallouts.reduce((acc, callout) => ({ ...acc, [callout.position - 1]: callout }), {})
      ),
    getSearchIsLoading: (getState, createSelector) =>
      createSelector([getState], ({ loading }) => loading),
    getSearchErrorMessage: (getState, createSelector) =>
      createSelector([getState], ({ errorMessage }) => errorMessage),
    getSearchResponse: (getState, createSelector) =>
      createSelector([getState], ({ fetchSearchResponse }) => fetchSearchResponse),
    getSearchResponseTotalNumberOfResults: (getState, createSelector) =>
      createSelector(
        [getState],
        ({ fetchSearchResponse }) =>
          (fetchSearchResponse.count || 0) +
          (fetchSearchResponse.alternateSearch
            ? fetchSearchResponse.alternateSearch.results.length
            : 0) +
          (fetchSearchResponse.bestMatchSearch
            ? fetchSearchResponse.bestMatchSearch.results.length
            : 0)
      ),
    getShouldPreferCatReviews: (getState, createSelector, { getSearchResponse }) =>
      createSelector(
        [getSearchResponse],
        ({ derivedData }) => derivedData?.shouldPreferCatReviews || false
      ),
    getSearchCurrencyCode: (getState, createSelector, { getSearchResponse }) =>
      createSelector(
        [getSearchResponse],
        ({ derivedData }) => derivedData?.currencyIso || DEFAULT_CURRENCY_ISO
      ),
    getSearchPriceFilterMaxValue: (getState, createSelector, { getSearchResponse }) =>
      createSelector(
        [getSearchResponse],
        ({ derivedData }) => derivedData?.priceFilterMaxValue || PRICE_FILTER.DEFAULT_MAX_PRICE
      ),
    getSearchPriceFilterMinValue: (getState, createSelector, { getSearchResponse }) =>
      createSelector(
        [getSearchResponse],
        ({ derivedData }) => derivedData?.priceFilterMinValue || PRICE_FILTER.DEFAULT_MIN_PRICE
      ),
    getPagination: (getState, createSelector, { getSearchResponse }) =>
      createSelector([getSearchResponse], ({ query, count, pageCount }) => ({
        page: query ? query.page : 1,
        count,
        pageCount,
      })),
    getSearchInitiatedByMap: (getState, createSelector) =>
      createSelector([getState], (x) => x.searchInitiatedByMap),
    getZoomLevel: (getState, createSelector, { getSearchResponse }) =>
      createSelector([getSearchResponse], ({ derivedData }) => derivedData?.zoomlevel),
    getCenterLatLng: (getState, createSelector, { getSearchResponse }) =>
      createSelector([getSearchResponse], ({ derivedData }) => {
        return derivedData?.center
          ? {
              lat: derivedData.center[0],
              lng: derivedData.center[1],
            }
          : null;
      }),
    getExcludedPersonOpks: (getState, createSelector, { getSearchResponse }) =>
      createSelector([getSearchResponse], ({ derivedData }) => derivedData?.excludedPersonOpks),
    getIsBestMatchExperience: (getState, createSelector, { getSearchResponse }) =>
      createSelector(
        [getSearchResponse],
        ({ derivedData }) => derivedData?.showBestMatchExperience ?? false
      ),
    getSearchFiltersOptions: (getState, createSelector) =>
      createSelector([getState], (x) => x.options),
    getHasPets: (getState, createSelector) =>
      createSelector([getState], (x) => !!x.options[OVERNIGHT_BOARDING().type].pets),
    getMapData: (getState, createSelector, { getSearchResponse }) =>
      createSelector([getSearchResponse], ({ viewport, derivedData }) => ({
        ...derivedData,
        viewport,
      })),
    getQueryPetTypes: (getState, createSelector, { getSearchQuery }) =>
      createSelector([getSearchQuery], (x) => (x?.petType?.length ? x?.petType.split(',') : [])),
    getQueryDateRange: (getState, createSelector, { getSearchQuery }) =>
      createSelector(
        [getSearchQuery, frontendConfigurationSelectors.getLanguage],
        (searchQuery, language) => {
          if (!searchQuery) return {};
          const startDate = parseDate(searchQuery.startDate, language);
          const endDate = parseDate(searchQuery.endDate, language);
          return {
            startDate: startDate instanceof Date ? startDate : undefined,
            endDate: endDate instanceof Date ? endDate : undefined,
          };
        }
      ),
    getQueryDistinctDates: (getState, createSelector, { getSearchQuery }) =>
      createSelector(
        [getSearchQuery, frontendConfigurationSelectors.getLanguage],
        (searchQuery, language) => {
          if (!searchQuery) return {};
          const dateList = searchQuery.dates;
          return {
            dates: dateList ? dateList.map((date) => parseDate(date, language)) : undefined,
          };
        }
      ),
    getQueryDerivedData: (getState, createSelector) =>
      createSelector([getState], ({ fetchSearchResponse }) => fetchSearchResponse.derivedData),
    getSearchResultsHasFacilities: (getState, createSelector, { getSearchResults }) =>
      createSelector([getSearchResults], (searchResults) =>
        searchResults.some((result) => result.isFacility)
      ),
    getPriceTransparencyLevel: (getState, createSelector, { getQueryDerivedData }) =>
      createSelector([getQueryDerivedData], (derivedData) => derivedData.priceTransparencyLevel),
    getIsShowingPriceInCurrency: (getState, createSelector) =>
      createSelector([getState], (state) => state.isShowingPriceInCurrency),
  },
};

export const { actions, selectors, effects, reducer } = createDuck(options) as Duck<
  State,
  Actions,
  Effects,
  Selectors
>;
